import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  // Tooltip, // Non utilisé
  Paper,
  IconButton,
  useTheme,
  Fade,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from "recharts";
import CloseIcon from '@mui/icons-material/Close';
import TableChartIcon from '@mui/icons-material/TableChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import StorefrontIcon from '@mui/icons-material/Storefront';

const ProvinceModal = ({ province, onClose, isMobile }) => {
  const [viewMode, setViewMode] = useState("culture"); // Vue par défaut changée à culture
  const theme = useTheme();

  if (!province) return null;
  const isEstuaire = province.id === "GA1" || province.name === "Estuaire";
  const dataUnavailable = Boolean(province.dataUnavailable ?? !isEstuaire);

  // Gestion du changement de vue
  const handleViewModeChange = (event, newView) => {
    if (newView) setViewMode(newView);
  };

  // Calcul de la production totale pour la province
  const totalProduction = (province.stats || []).reduce(
    (total, stat) => total + stat.production,
    0
  );
  
  // Générer des couleurs pour le graphique
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];
  
  // Préparer les données pour le graphique
  const pieData = (province.stats || []).map((stat, index) => ({
    name: stat.culture,
    value: stat.production,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <Modal 
      open={!!province} 
      onClose={onClose}
      closeAfterTransition
    >
      <Fade in={!!province}>
        <Paper
          elevation={5}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: { xs: 2, sm: 4 },
            borderRadius: "12px",
            width: isMobile ? "95%" : "90%",
            maxWidth: isMobile ? "none" : "500px",
            maxHeight: isMobile ? "80vh" : "90vh",
            overflow: "auto",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              Province: {province.name}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.grey[500] }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Sélecteur Culture / Pourcentage ou Information Phase 2 */}
          {dataUnavailable ? (
            <Box sx={{ mb: 2 }}>
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  bgcolor: "#f0fdf4",
                  color: "#166534",
                  border: "1px solid #bbf7d0",
                  fontWeight: 600,
                  '& .MuiAlert-icon': {
                    color: "#16a34a"
                  }
                }}
              >
                Données actives uniquement pour l'Estuaire
              </Alert>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 2.5, 
                  bgcolor: alpha(theme.palette.grey[500], 0.05),
                  border: `1px solid ${alpha(theme.palette.grey[500], 0.15)}`,
                  borderRadius: "8px"
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: "0.95rem", lineHeight: 1.7 }}>
                  Dans le cadre du lancement d'Agri Vision Gabon, les données de production agricole sont actuellement actives et vérifiées uniquement pour la <strong>Province de l'Estuaire</strong> (Grand Libreville : Akanda, Owendo et Libreville).
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem", lineHeight: 1.7 }}>
                  Les indicateurs de production pour la <strong>Province de {province.name}</strong> seront collectés et fiabilisés lors de la <strong>Phase 2</strong> de déploiement à l'échelle nationale.
                </Typography>
              </Paper>
            </Box>
          ) : (
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size={isMobile ? "small" : "medium"}
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "center",
                width: "100%",
                '& .MuiToggleButton-root': {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    fontWeight: 600
                  }
                }
              }}
            >
              <ToggleButton value="culture" sx={{ flex: 1 }}>
                <TableChartIcon sx={{ mr: 1, fontSize: '1rem' }} />
                Tableau
              </ToggleButton>
              <ToggleButton value="pourcentage" sx={{ flex: 1 }}>
                <PieChartIcon sx={{ mr: 1, fontSize: '1rem' }} />
                Graphique
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* Vue par Culture (Tableau) */}
          {!dataUnavailable && viewMode === "culture" && (
            <Paper elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, borderRadius: '8px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Culture</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Production</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unité</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(province.stats || []).map((stat, index) => (
                    <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.action.hover, 0.1) } }}>
                      <TableCell>{stat.culture}</TableCell>
                      <TableCell>{stat.production.toLocaleString()}</TableCell>
                      <TableCell>{stat.unite}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05), fontWeight: 'bold' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{totalProduction.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>tonnes</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Vue Pourcentage (Graphique) */}
          {!dataUnavailable && viewMode === "pourcentage" && (
            <Box sx={{ height: isMobile ? 250 : 300, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={isMobile ? false : true}
                    outerRadius={isMobile ? 70 : 80}
                    fill="#8884d8"
                    dataKey="value"
                    label={isMobile ? undefined : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend layout="vertical" verticalAlign="middle" align="right" />
                  <RechartsTooltip formatter={(value) => `${value.toLocaleString()} tonnes`} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          )}
          
          {/* Tableau des pourcentages sous le graphique */}
          {!dataUnavailable && viewMode === "pourcentage" && (
            <Paper elevation={0} sx={{ mt: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, borderRadius: '8px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Culture</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Pourcentage</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Production</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(province.stats || []).map((stat, index) => {
                    const percentage = (stat.production / totalProduction) * 100;
                    return (
                      <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.action.hover, 0.1) } }}>
                        <TableCell>{stat.culture}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={percentage}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  backgroundColor: alpha(theme.palette.grey[300], 0.5),
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: pieData[index]?.color || theme.palette.primary.main,
                                  },
                                }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography variant="body2" color="text.secondary">
                                {`${Math.round(percentage)}%`}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{stat.production.toLocaleString()} {stat.unite}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Bouton d'accès direct aux récoltes de la province dans la Centrale d'Achat */}
          <Button
            disabled
            variant="contained"
            startIcon={<StorefrontIcon sx={{ color: "#9ca3af !important" }} />}
            sx={{
              mt: 2.5,
              mb: 1,
              bgcolor: "#f3f4f6 !important",
              color: "#6b7280 !important",
              border: "1px solid #d1d5db",
              fontWeight: 700,
              textTransform: "none",
              py: 1,
              px: 2,
              boxShadow: "none !important",
              cursor: "not-allowed !important",
              pointerEvents: "auto !important",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
            fullWidth
          >
            <span>🛒 Récoltes Centrale d'Achat ({province.name})</span>
            <Box
              component="span"
              sx={{
                bgcolor: "#9ca3af",
                color: "#ffffff",
                fontSize: "0.68rem",
                fontWeight: 700,
                px: 1,
                py: 0.3,
                borderRadius: "10px",
                textTransform: "none"
              }}
            >
              Bientôt disponible
            </Box>
          </Button>

          <Button
            variant="outlined"
            sx={{ 
              mt: 0.5,
              borderColor: alpha(theme.palette.grey[500], 0.4),
              color: theme.palette.text.secondary,
              textTransform: "none",
              '&:hover': {
                borderColor: theme.palette.grey[600],
                bgcolor: alpha(theme.palette.grey[500], 0.05)
              }
            }}
            onClick={onClose}
            fullWidth
          >
            Fermer
          </Button>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default ProvinceModal;
